/**
 * Storefront Theme - Global JavaScript
 */

// Utility Functions
const debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

const fetchConfig = (type = 'json') => {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': `application/${type}`
    }
  };
};

// Active focus trap, tracked so the listener can actually be removed again.
let activeTrap = null;

// Trap focus within modal/drawer
const trapFocus = (container, elementToFocus = container) => {
  // Clear any existing trap first, otherwise handlers accumulate on every open.
  removeTrapFocus();

  const handler = (e) => {
    if (e.key !== 'Tab') return;

    // Queried per keypress so dynamically added rows are included.
    const focusableElements = Array.from(
      container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute('hidden') && el.offsetParent !== null);

    if (!focusableElements.length) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        lastFocusable.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        firstFocusable.focus();
        e.preventDefault();
      }
    }
  };

  container.addEventListener('keydown', handler);
  activeTrap = { container, handler };

  elementToFocus.focus();
};

// Remove focus trap
const removeTrapFocus = (elementToFocus = null) => {
  if (activeTrap) {
    activeTrap.container.removeEventListener('keydown', activeTrap.handler);
    activeTrap = null;
  }
  if (elementToFocus) elementToFocus.focus();
};

// Publish/Subscribe pattern for component communication
class PubSub {
  constructor() {
    this.events = {};
  }

  subscribe(event, callback) {
    if (!this.events[event]) this.events[event] = [];
    this.events[event].push(callback);
    return () => this.unsubscribe(event, callback);
  }

  unsubscribe(event, callback) {
    if (!this.events[event]) return;
    this.events[event] = this.events[event].filter(cb => cb !== callback);
  }

  publish(event, data) {
    if (!this.events[event]) return;
    this.events[event].forEach(callback => callback(data));
  }
}

window.Storefront = window.Storefront || {};
window.Storefront.pubsub = new PubSub();

// Header functionality
class StickyHeader extends HTMLElement {
  constructor() {
    super();
    this.header = this.querySelector('.header-wrapper') || this;
    this.headerHeight = this.header.offsetHeight;
    this.currentScrollTop = 0;
    this.lastScrollTop = 0;
  }

  connectedCallback() {
    this.onScrollHandler = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  onScroll() {
    this.currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (this.currentScrollTop > this.headerHeight) {
      this.header.classList.add('is-scrolled');
    } else {
      this.header.classList.remove('is-scrolled');
    }

    this.lastScrollTop = this.currentScrollTop;
  }
}

customElements.define('sticky-header', StickyHeader);

// Mobile Menu
class MobileMenu {
  constructor() {
    this.mobileNav = document.querySelector('[data-mobile-nav]');
    this.overlay = document.querySelector('[data-overlay]');
    this.toggleButtons = document.querySelectorAll('[data-mobile-menu-toggle]');
    this.closeButton = document.querySelector('[data-mobile-menu-close]');
    this.submenuToggles = document.querySelectorAll('[data-mobile-submenu-toggle]');

    if (!this.mobileNav) return;

    // Every listener is registered against this signal so destroy() can remove
    // them all at once. The Escape handler binds to `document`, which outlives
    // the section, so without teardown each re-render would leak one.
    this.controller = new AbortController();

    this.init();
  }

  init() {
    const { signal } = this.controller;

    this.toggleButtons.forEach(btn => {
      btn.addEventListener('click', () => this.toggle(), { signal });
    });

    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close(), { signal });
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close(), { signal });
    }

    this.submenuToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => this.toggleSubmenu(e), { signal });
    });

    // Nested submenu toggles (3rd level)
    document.querySelectorAll('[data-nested-toggle]').forEach(toggle => {
      toggle.addEventListener('click', (e) => this.toggleNested(e), { signal });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    }, { signal });
  }

  destroy() {
    this.controller?.abort();
  }

  toggle() {
    this.mobileNav.classList.toggle('is-open');
    this.overlay?.classList.toggle('is-active');
    document.body.classList.toggle('overflow-hidden');

    const isOpen = this.mobileNav.classList.contains('is-open');
    this.toggleButtons.forEach(btn => {
      btn.setAttribute('aria-expanded', isOpen);
    });

    if (isOpen) {
      trapFocus(this.mobileNav);
    }
  }

  close() {
    const wasOpen = this.mobileNav.classList.contains('is-open');

    this.mobileNav.classList.remove('is-open');
    this.overlay?.classList.remove('is-active');
    document.body.classList.remove('overflow-hidden');

    this.toggleButtons.forEach(btn => {
      btn.setAttribute('aria-expanded', 'false');
    });

    // Release the trap and hand focus back to the button that opened it.
    if (wasOpen) {
      removeTrapFocus(this.toggleButtons[0] || null);
    }
  }

  toggleSubmenu(e) {
    const toggle = e.currentTarget;
    const item = toggle.closest('.mobile-nav__item');
    const isOpen = item.classList.contains('is-open');

    // Close all other submenus
    document.querySelectorAll('.mobile-nav__item.is-open').forEach(openItem => {
      if (openItem !== item) {
        openItem.classList.remove('is-open');
        openItem.querySelector('[data-mobile-submenu-toggle]')?.setAttribute('aria-expanded', 'false');
      }
    });

    item.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', !isOpen);
  }

  toggleNested(e) {
    const toggle = e.currentTarget;
    const item = toggle.closest('.mobile-nav__submenu-item');
    const isOpen = item.classList.contains('is-nested-open');
    item.classList.toggle('is-nested-open');
    toggle.setAttribute('aria-expanded', !isOpen);
  }
}

// Cart functionality
class CartManager {
  constructor() {
    this.cartDrawer = document.querySelector('[data-cart-drawer]');
    this.cartToggle = document.querySelectorAll('[data-cart-toggle]');
    this.cartCount = document.querySelectorAll('[data-cart-count]');

    // The click/submit delegates below live on `document`. Without teardown a
    // second instance would double-handle add-to-cart and add the item twice.
    this.controller = new AbortController();

    this.init();
  }

  destroy() {
    this.controller?.abort();
  }

  init() {
    const { signal } = this.controller;

    // Add to cart buttons (click delegation)
    document.addEventListener('click', (e) => {
      const addToCartBtn = e.target.closest('[data-add-to-cart]');
      if (addToCartBtn) {
        e.preventDefault();

        let variantId = addToCartBtn.dataset.addToCart;
        let quantity = 1;

        // If no variant ID on button, get it from the closest form
        if (!variantId) {
          const form = addToCartBtn.closest('form');
          const input = form?.querySelector('input[name="id"], [data-variant-id]');
          variantId = input?.value;
          quantity = parseInt(form?.querySelector('input[name="quantity"]')?.value || '1', 10);
        }

        if (!variantId) return;
        this.addToCart(parseInt(variantId, 10), quantity);
      }
    }, { signal });

    // Intercept product form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form.hasAttribute('data-product-form')) return;

      e.preventDefault();
      const variantId = form.querySelector('input[name="id"]')?.value;
      const quantity = parseInt(form.querySelector('input[name="quantity"]')?.value || '1', 10);
      if (variantId) {
        this.addToCart(parseInt(variantId, 10), quantity);
      }
    }, { signal });

    // Cart toggle (header cart icon)
    this.cartToggle.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.cartDrawer?.classList.contains('is-open')) {
          this.cartDrawer.classList.remove('is-open');
          document.body.style.overflow = '';
        } else {
          this.cartDrawer?.classList.add('is-open');
          document.body.style.overflow = 'hidden';
        }
      }, { signal });
    });
  }

  async addToCart(variantId, quantity = 1) {
    try {
      const response = await fetch(window.routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          items: [{ id: variantId, quantity }]
        })
      });

      const data = await response.json();

      if (data.status === 422) {
        throw new Error(data.description);
      }

      this.updateCartCount();
      window.Storefront.pubsub.publish('cart:add', data);

      // Dispatch DOM event — cart-drawer snippet handles open + refresh
      document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));

    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch(window.routes.cart_url + '.js');
      const cart = await response.json();

      this.cartCount.forEach(el => {
        el.textContent = cart.item_count;
      });

      window.Storefront.pubsub.publish('cart:update', cart);
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }
}

// Scroll animations
class ScrollAnimations {
  constructor() {
    this.elements = document.querySelectorAll('[data-animate]');
    if (!this.elements.length) return;

    this.init();
  }

  init() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    this.elements.forEach(el => observer.observe(el));
  }
}

// Back to top button
class BackToTop {
  constructor() {
    this.button = document.querySelector('[data-back-to-top]');
    if (!this.button) return;

    this.init();
  }

  init() {
    window.addEventListener('scroll', debounce(() => {
      if (window.pageYOffset > 500) {
        this.button.classList.add('is-visible');
      } else {
        this.button.classList.remove('is-visible');
      }
    }, 100), { passive: true });

    this.button.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }
}

// Footer accordion (mobile)
class FooterAccordion {
  constructor() {
    this.toggles = document.querySelectorAll('[data-footer-toggle]');
    if (!this.toggles.length) return;

    this.init();
  }

  init() {
    this.toggles.forEach(toggle => {
      toggle.addEventListener('click', () => {
        if (window.innerWidth >= 750) return;

        const block = toggle.closest('.footer__block--collapsible');
        block.classList.toggle('is-open');
      });
    });
  }
}

// Components that must be rebuilt when the theme editor swaps a section's DOM.
// Tracked so the previous instance can be torn down instead of left leaking.
let mobileMenuInstance = null;
let cartManagerInstance = null;

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  mobileMenuInstance = new MobileMenu();
  cartManagerInstance = new CartManager();
  new ScrollAnimations();
  new BackToTop();
  new FooterAccordion();
});

// The theme editor replaces a section's DOM on edit, which discards the elements
// the old instance was bound to. Destroy it first, then rebuild against the new
// DOM, otherwise document-level listeners accumulate on every edit.
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  if (!section) return;

  if (section.querySelector('[data-mobile-nav]')) {
    removeTrapFocus();
    mobileMenuInstance?.destroy();
    mobileMenuInstance = new MobileMenu();
  }

  // CartManager caches its [data-cart-count] nodes at construction, and the
  // drawer contributes one, so it has to be rebuilt when the header re-renders.
  if (section.querySelector('[data-cart-count]')) {
    cartManagerInstance?.destroy();
    cartManagerInstance = new CartManager();
  }

  if (section.querySelector('.footer__block--collapsible')) {
    new FooterAccordion();
  }
});

// Expose utilities globally
window.Storefront.debounce = debounce;
window.Storefront.fetchConfig = fetchConfig;
window.Storefront.trapFocus = trapFocus;
window.Storefront.removeTrapFocus = removeTrapFocus;
